import React, { useState, useEffect } from 'react';
import { getTexts } from '../config/texts';
import { getUserWorks, getTaskProgress } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './MyWorks.css';

const MyWorks = ({ language, isEmbedded = false }) => {
  const { user } = useAuth();
  const t = getTexts('myWorks', language);

  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [progressTimers, setProgressTimers] = useState({});

  // 加载作品列表
  const loadWorks = async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);

    try {
      // v3.0 - 使用系统统一 user_id（数据库主键）
      const userId = user?.user_id || user?.userId;
      console.log('📚 Loading works for userId:', userId, 'Type:', typeof userId, 'Full user:', user);
      if (!userId) {
        console.error('No user ID found', user);
        return;
      }

      const result = await getUserWorks(userId);
      if (result.code === 200) {
        const worksData = result.data || [];

        // 按创建时间倒序排列
        worksData.sort((a, b) => {
          const timeA = new Date(a.created_time || 0).getTime();
          const timeB = new Date(b.created_time || 0).getTime();
          return timeB - timeA;
        });

        setWorks(worksData);

        // 为正在生成的作品设置进度查询定时器
        setupProgressTimers(worksData);
      }
    } catch (error) {
      console.error('Failed to load works:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // 设置进度查询定时器
  const setupProgressTimers = (worksData) => {
    // 清除旧的定时器
    Object.values(progressTimers).forEach(timer => clearInterval(timer));
    const newTimers = {};

    worksData.forEach(work => {
      if (work.status !== 'finished' && work.status !== 'failed') {
        // 立即查询一次
        queryWorkProgress(work.task_id);

        // 设置定时查询（每5分钟）
        const timer = setInterval(() => {
          queryWorkProgress(work.task_id);
        }, 5 * 60 * 1000);

        newTimers[work.task_id] = timer;
      }
    });

    setProgressTimers(newTimers);
  };

  // 查询单个作品进度
  const queryWorkProgress = async (taskId) => {
    try {
      const userId = user?.email || user?.google_id || user?.kuaishou_id;
      const result = await getTaskProgress(taskId, userId);

      if (result.code === 200) {
        const data = result.data;

        // 更新作品状态
        setWorks(prevWorks => prevWorks.map(work => {
          if (work.task_id === taskId) {
            const updated = { ...work, ...data };

            // 如果任务已完成或失败，清除定时器
            if (data.status === 'finished' || data.status === 'failed') {
              if (progressTimers[taskId]) {
                clearInterval(progressTimers[taskId]);
                setProgressTimers(prev => {
                  const newTimers = { ...prev };
                  delete newTimers[taskId];
                  return newTimers;
                });
              }
            }

            return updated;
          }
          return work;
        }));
      }
    } catch (error) {
      console.error('Failed to query progress:', error);
    }
  };

  // 初始加载
  useEffect(() => {
    if (user) {
      loadWorks();
    }
  }, [user]);

  // 组件卸载时清除所有定时器
  useEffect(() => {
    return () => {
      Object.values(progressTimers).forEach(timer => clearInterval(timer));
    };
  }, [progressTimers]);

  // 下拉刷新
  const handleRefresh = () => {
    loadWorks(true);
  };

  // 获取进度百分比
  const getProgressPercent = (work) => {
    // 使用合成进度作为总体进度
    return work.synth_progress || work.video_progress || work.song_progress || 0;
  };

  // 获取状态文本
  const getStatusText = (status) => {
    const statusMap = {
      'pending': '等待中',
      'song_start': '生成歌曲中',
      'song_progress': '生成歌曲中',
      'song_finished': '歌曲完成',
      'video_start': '生成视频中',
      'video_progress': '生成视频中',
      'video_finished': '视频完成',
      'synth_start': '合成中',
      'synth_progress': '合成中',
      'finished': '已完成',
      'failed': '生成失败'
    };
    return statusMap[status] || status;
  };

  // 播放视频
  const handlePlay = (work, e) => {
    if (e) e.stopPropagation(); // 阻止事件冒泡

    if (work.status === 'finished' && work.synth_url) {
      // 打开新标签页播放视频
      window.open(work.synth_url, '_blank');
    } else {
      alert(t.workInProgress || '作品正在生成中，请稍后再试');
    }
  };

  // 下载视频
  const handleDownload = (work, e) => {
    if (e) e.stopPropagation();

    if (work.status === 'finished' && work.synth_url) {
      const link = document.createElement('a');
      link.href = work.synth_url;
      link.download = `${work.title || 'video'}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      alert(t.workInProgress || '作品正在生成中，请稍后再试');
    }
  };

  // 分享视频
  const handleShare = async (work, e) => {
    if (e) e.stopPropagation();

    if (work.status !== 'finished') {
      alert(t.workInProgress || '作品正在生成中，请稍后再试');
      return;
    }

    // 检查是否支持Web Share API（移动端和现代浏览器）
    if (navigator.share && work.synth_url) {
      try {
        await navigator.share({
          title: work.title || '我的MV作品',
          text: work.topic || '看看我创作的MV视频！',
          url: work.synth_url
        });
      } catch (err) {
        // 用户取消分享或出错，fallback到复制链接
        copyShareLink(work.synth_url);
      }
    } else {
      // 桌面端或不支持Share API，复制链接到剪贴板
      copyShareLink(work.synth_url);
    }
  };

  // 复制分享链接
  const copyShareLink = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      alert('链接已复制到剪贴板，可以分享给朋友了！');
    }).catch(() => {
      // Fallback: 创建临时输入框复制
      const tempInput = document.createElement('input');
      tempInput.value = url;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand('copy');
      document.body.removeChild(tempInput);
      alert('链接已复制到剪贴板，可以分享给朋友了！');
    });
  };

  if (loading) {
    return (
      <div className={`my-works-container ${isEmbedded ? 'embedded' : ''}`}>
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>{t.loading || '加载中...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`my-works-container ${isEmbedded ? 'embedded' : ''}`}>
      {!isEmbedded && (
        <div className="my-works-header">
          <h2>{t.title || '我的作品'}</h2>
          <button className="refresh-btn" onClick={handleRefresh} disabled={refreshing}>
            <i className={`fas fa-sync-alt ${refreshing ? 'spinning' : ''}`}></i>
            {t.refresh || '刷新'}
          </button>
        </div>
      )}

      {works.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-video-slash"></i>
          <p>{t.empty || '暂无作品，快去创建你的第一个视频吧！'}</p>
        </div>
      ) : (
        <div className="works-grid">
          {works.map(work => (
            <div key={work.task_id} className="work-card">
              {/* 缩略图 - 可点击播放 */}
              <div
                className="work-thumbnail"
                onClick={work.status === 'finished' ? (e) => handlePlay(work, e) : undefined}
                style={work.status === 'finished' ? { cursor: 'pointer' } : {}}
              >
                <img src={work.thumb_url || '/pic/story1.jpg'} alt={work.title || '作品'} />
                {work.status !== 'finished' && (
                  <div className="work-status-overlay">
                    <span className="status-badge">{getStatusText(work.status)}</span>
                  </div>
                )}
                {work.status === 'finished' && (
                  <div className="work-play-overlay">
                    <i className="fas fa-play-circle"></i>
                  </div>
                )}
              </div>

              {/* 进度条（仅对未完成的作品） */}
              {work.status !== 'finished' && work.status !== 'failed' && (
                <div className="work-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${getProgressPercent(work)}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">{getProgressPercent(work)}%</span>
                </div>
              )}

              {/* 作品信息 */}
              <div className="work-info">
                <h3 className="work-title">{work.title || '未命名作品'}</h3>
                <p className="work-topic">{work.topic || ''}</p>
                <p className="work-time">
                  {new Date(work.created_time).toLocaleString()}
                </p>
                {work.reason && work.status === 'failed' && (
                  <p className="work-error">{work.reason}</p>
                )}
              </div>

              {/* 操作按钮 */}
              {work.status === 'finished' && (
                <div className="work-actions">
                  <button className="action-btn share-btn" onClick={(e) => handleShare(work, e)}>
                    <i className="fas fa-share-alt"></i>
                    {t.share || '分享'}
                  </button>
                  <button className="action-btn download-btn" onClick={(e) => handleDownload(work, e)}>
                    <i className="fas fa-download"></i>
                    {t.download || '下载'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyWorks;
